import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

// @mui material components
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Card,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';
import axios from 'axios';
import PapaParse from 'papaparse';
import { toast } from 'react-toastify';

// Material Dashboard 2 React components
import DownloadMenu from 'components/DownloadMenu/DownloadMenu';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import DataTable from 'examples/Tables/DataTable';
import AsyncMultiDropdown from 'layouts/TransactionsManagement/components/AsyncMultiDropdown';

// Data
import { columns, rows } from 'components/PropertyListTable/data';
import { downloadCSV, downloadXLSX } from 'helpers/fileDownload';
import { exportPropertiesService, getPropertiesList } from 'services/addProperty.service';
import { propertiesColumns, propertiesRows } from './Rows';

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 400,
    },
  },
};

const Properties = () => {
  const navigate = useNavigate();
  const [tableLoader, setTableLoader] = useState(false);
  const [propertiesList, setPropertiesList] = useState({ properties: [] });
  const [currentPage, setCurrentPage] = useState(0);
  const [query, setQuery] = useState({
    propertyType: [],
    propertyTag: '',
    propertyStatus: '',
    regulationType: '',
    sort: '',
    search: '',
    page: 0,
    perPage: 10,
  });

  const [loadingCSV, setLoadingCSV] = useState(false);
  const [loadingXLSX, setLoadingXLSX] = useState(false);

  //handler functions
  const handleChangeType = (value) => {
    setQuery((state) => {
      return {
        ...state,
        page: 0,
        propertyType:
          value?.length > 0 ? [...value.map((val) => val?.propertyTypeId)] : [],
      };
    });
    setCurrentPage(0);
  };
  //API Integration
  const fetchPropertiesInfo = async (query, cancelToken) => {
    setTableLoader(true);
    try {
      const res = await getPropertiesList(query, cancelToken);
      if (!res.isError) {
        setPropertiesList({ ...res.data });
      } else {
        toast.error(res?.message);
      }
    } catch (error) {
      const { response } = error;
      response?.data?.message && toast.error(response?.data?.message);
    }
    setTableLoader(false);
  };
  useEffect(() => {
    const source = axios.CancelToken.source();
    fetchPropertiesInfo(query, source.token);
    return () => source.cancel('Component got unmounted');
  }, [query]);
  useEffect(() => {
    if (currentPage !== query.page && !isNaN(currentPage)) {
      setQuery((state) => {
        return { ...state, page: currentPage };
      });
    }
  }, [currentPage]);
 
  const fetchDownloadProperties = async (signal) => {
    try {
      const response = await exportPropertiesService(query, signal); // todo:
      
      if (!response?.data?.isError) {
        return response?.data?.data?.properties ?? []
      }
      throw new Error('Unable to download file!')
    } catch (error) {
      throw error;
    }
  };

  const onDownloadCSV = async () => {

    if (loadingCSV) return
    setLoadingCSV(true)

    try {
      const properties = await fetchDownloadProperties();

      const csv = PapaParse.unparse(
        properties.map(propertiesRows),
        {
          header: false,
        }
      );

      const headerParser = PapaParse.unparse(
        {
          fields: propertiesColumns(),
          data: [],
        },
        {
          header: true,
        }
      );

      const formattedCSVData = headerParser + csv;

      if (!properties?.length) {
        toast.warn(
          `Unable to download file as there are no records to export!`
        );
        return;
      }

      await downloadCSV(
        formattedCSVData,
        `Properties`
      );
    } catch (error) {
      console.log({ error });
      toast.error(error?.response?.data?.message ?? 'Something went wrong!');
    }
    finally {
      setLoadingCSV(false)
    }
  };

  const onDownloadXLSX = async () => {
    
    if (loadingXLSX) return
    setLoadingXLSX(true)
    
    try {
      const properties = await fetchDownloadProperties();

      if (!properties?.length) {
        toast.warn(
          `Unable to download file as there are no records to export!`
        );
        return;
      }

      await downloadXLSX(
        properties.map(propertiesRows),
        propertiesColumns(),
        `Properties`
      );
    } catch (error) {
      console.log({error});
      toast.error(error?.response?.data?.message ?? 'Something went wrong!');
    }
    finally {
      setLoadingXLSX(false)
    }
  }

  return (
    <MDBox pt={6} pb={3}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                Properties
              </MDTypography>
              <MDButton
                variant="outlined"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() =>
                  navigate('/properties/addProperty')
                }
              >
                Add New Property
              </MDButton>
            </MDBox>
            <Grid container spacing={4} p={3}>
              <Grid item xs={12} md={6} lg={4} xl={3}>
                <FormControl fullWidth>
                  <InputLabel id="sort-property">Sort Property</InputLabel>
                  <Select
                    labelId="sort-property"
                    id="sort-property"
                    value={query.sort}
                    onChange={(e) => {
                      setQuery((state) => {
                        return { ...state, page: 0, sort: e.target.value };
                      });
                      setCurrentPage(0);
                    }}
                    input={<OutlinedInput label="Sort Property" />}
                    endAdornment={
                      <IconButton
                        sx={{
                          visibility: query.sort ? 'visible' : 'hidden',
                          position: 'absolute',
                          right: '25px',
                          marginRight: '0',
                        }}
                        style={{
                          backgroundColor: 'white',
                        }}
                        onClick={() => {
                          setQuery((state) => {
                            return { ...state, page: 0, sort: '' };
                          });
                          setCurrentPage(0);
                        }}
                        size="small"
                      >
                        <ClearIcon />
                      </IconButton>
                    }
                  >
                    <MenuItem value="highestToLowestFunded" key="highest">
                      Highest to Lowest Funded
                    </MenuItem>
                    <MenuItem value="lowestToHighestFunded" key="lowest">
                      Lowest to Highest Funded
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6} lg={4} xl={3}>
                <AsyncMultiDropdown onChange={handleChangeType} />
              </Grid>

              <Grid item xs={12} md={6} lg={4} xl={3}>
                <FormControl sx={{ minHeight: 48 }} fullWidth>
                  <InputLabel id="rent-status-label">Rent status</InputLabel>
                  <Select
                    labelId="rent-status-label"
                    id="rent-status"
                    value={query.propertyStatus}
                    onChange={(e) => {
                      setQuery((state) => {
                        return {
                          ...state,
                          page: 0,
                          propertyStatus: e.target.value,
                        };
                      });
                      setCurrentPage(0);
                    }}
                    sx={{ minHeight: 48 }}
                    input={<OutlinedInput label="Rent status" />}
                    MenuProps={MenuProps}
                    endAdornment={
                      <IconButton
                        sx={{
                          visibility: query.propertyStatus
                            ? 'visible'
                            : 'hidden',
                          position: 'absolute',
                          right: '25px',
                          marginRight: '0',
                        }}
                        style={{
                          backgroundColor: 'white',
                        }}
                        onClick={() => {
                          setQuery((state) => {
                            return {
                              ...state,
                              page: 0,
                              propertyStatus: '',
                            };
                          });
                          setCurrentPage(0);
                        }}
                        size="small"
                      >
                        <ClearIcon />
                      </IconButton>
                    }
                  >
                    <MenuItem value={'rentReady'}>Rent Ready</MenuItem>
                    <MenuItem value={'rented'}>Rented</MenuItem>
                    <MenuItem value={'marketedForRent'}>
                      Marketed For Rent
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6} lg={4} xl={3}>
                <FormControl sx={{ minHeight: 48 }} fullWidth>
                  <InputLabel id="tag">Tag</InputLabel>
                  <Select
                    labelId="tag"
                    id="tag"
                    value={query.propertyTag}
                    onChange={(e) => {
                      setQuery((state) => {
                        return {
                          ...state,
                          page: 0,
                          propertyTag: e.target.value,
                        };
                      });
                      setCurrentPage(0);
                    }}
                    sx={{ minHeight: 48 }}
                    input={<OutlinedInput label="Tag" />}
                    MenuProps={MenuProps}
                    endAdornment={
                      <IconButton
                        sx={{
                          visibility: query.propertyTag ? 'visible' : 'hidden',
                          position: 'absolute',
                          right: '25px',
                          marginRight: '0',
                        }}
                        style={{
                          backgroundColor: 'white',
                        }}
                        onClick={() => {
                          setQuery((state) => {
                            return {
                              ...state,
                              page: 0,
                              propertyTag: '',
                            };
                          });
                          setCurrentPage(0);
                        }}
                        size="small"
                      >
                        <ClearIcon />
                      </IconButton>
                    }
                  >
                    <MenuItem value={'new'}>New</MenuItem>
                    <MenuItem value={'comingSoon'}>Coming soon</MenuItem>
                    <MenuItem value={'trending'}>Trending</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6} lg={4} xl={3}>
                <FormControl sx={{ minHeight: 48 }} fullWidth>
                  <InputLabel id="rent-status-label">Regulation Type</InputLabel>
                  <Select
                    labelId="rent-status-label"
                    id="rent-status"
                    value={query.regulationType}
                    onChange={(e) => {
                      setQuery((state) => {
                        return {
                          ...state,
                          page: 0,
                          regulationType: e.target.value,
                        };
                      });
                      setCurrentPage(0);
                    }}
                    sx={{ minHeight: 48 }}
                    input={<OutlinedInput label="Regulation Type" />}
                    MenuProps={MenuProps}
                    endAdornment={
                      <IconButton
                        sx={{
                          visibility: query.regulationType
                            ? 'visible'
                            : 'hidden',
                          position: 'absolute',
                          right: '25px',
                          marginRight: '0',
                        }}
                        style={{
                          backgroundColor: 'white',
                        }}
                        onClick={() => {
                          setQuery((state) => {
                            return {
                              ...state,
                              page: 0,
                              regulationType: '',
                            };
                          });
                          setCurrentPage(0);
                        }}
                        size="small"
                      >
                        <ClearIcon />
                      </IconButton>
                    }
                  >
                    <MenuItem value={'RegA'}>RegA</MenuItem>
                    <MenuItem value={'RegD'}>RegD</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6} lg={4} xl={3}>
                <DownloadMenu
                  onDownloadCSVClick={onDownloadCSV}
                  onDownloadXLSXClick={onDownloadXLSX}
                />
              </Grid>

            </Grid>
            <MDBox
              sx={{
                position: 'relative',
                paddingTop: '85px',
                paddingBottom: {
                  xs: '150px',
                  sm: '95px',
                },
              }}
            >
              <DataTable
                table={{
                  columns,
                  rows: rows(propertiesList, () => fetchPropertiesInfo(query)),
                }}
                canSearch
                searchTerm={query.search}
                setSearchTerm={(search) => setQuery({ ...query, search })}
                isSorted={true}
                entriesPerPage={false}
                showTotalEntries={true}
                isLoading={tableLoader}
                perPage={10}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPage={propertiesList.totalPage}
                totalRecords={propertiesList.count}
                noEndBorder
              />
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
};

export default Properties;
